import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../config/siteConfig';

// Single source of truth for the production base URL (og:image absolute URLs).
// Canonical + og:url + twitter:url are emitted globally by <AutoCanonical/>.
const BASE_URL = SITE_URL;

export const SEOHead = ({ 
  title, 
  description, 
  canonicalPath,
  city, 
  type = 'website',
  schema = null,
  faqSchema = null,
  image = '/logo.png',
  breadcrumbs = null,
  noindex = false
}) => {
  const currentYear = new Date().getFullYear();
  
  // Generate dynamic title with year
  const pageTitle = title ? `${title} | MoveAbroadIQ` : 'MoveAbroadIQ - Data-Driven Relocation Intelligence';
  // Single source of truth for the meta description: use the page-specific one
  // when provided, else fall back to the site default. (Static index.html no
  // longer carries a description, so this is the only description tag rendered.)
  const DEFAULT_DESCRIPTION = 'Compare cost of living, salary requirements and expat lifestyle across global cities. Plan your move abroad with real data.';
  const metaDescription = description || DEFAULT_DESCRIPTION;
  // NOTE: canonical + og:url + twitter:url are emitted globally by <AutoCanonical/>
  // (single source of truth, production host only). SEOHead no longer renders them
  // to avoid duplicate/conflicting canonical tags.
  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;
  
  // Generate breadcrumb schema if provided
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  } : null;
  
  return (
    <Helmet>
      <title>{String(pageTitle)}</title>
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      <meta name="description" content={metaDescription} />

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="MoveAbroadIQ" />
      <meta property="og:image" content={imageUrl} />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@moveabroadiq" />
      
      {city && (
        <>
          <meta name="keywords" content={`${city}, cost of living, relocation, expat, digital nomad, move abroad, ${currentYear}`} />
          <meta property="og:locale" content="en_US" />
        </>
      )}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

// Generate Schema.org structured data for City pages
export const generateCitySchema = (city) => {
  if (!city) return null;
  
  const totalCost = (city.rent_1br || 0) + (city.groceries_cost || 0) + 
                   (city.transport_cost || 0) + (city.healthcare_cost || 0) + 
                   (city.internet_cost || 0);
  
  return {
    "@context": "https://schema.org",
    "@type": "City",
    "name": city.name,
    "containedInPlace": {
      "@type": "Country",
      "name": city.country_name || ''
    },
    "description": city.description || `Guide to living in ${city.name}`,
    "image": city.image_url,
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Cost of Living Index",
        "value": totalCost,
        "unitText": "USD/month"
      },
      {
        "@type": "PropertyValue",
        "name": "Safety Score",
        "value": city.safety_score || 0,
        "maxValue": 10
      },
      {
        "@type": "PropertyValue",
        "name": "NomadIQ Score",
        "value": city.nomad_score || 0,
        "maxValue": 10
      },
      {
        "@type": "PropertyValue",
        "name": "Internet Speed",
        "value": city.internet_speed || 0,
        "unitText": "Mbps"
      }
    ]
  };
};

// Generate Schema.org Article for SEO pages
export const generateArticleSchema = (city, pageType, url) => {
  if (!city) return null;
  
  const currentYear = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0];
  const totalCost = (city.rent_1br || 0) + (city.groceries_cost || 0) + 
                   (city.transport_cost || 0) + (city.healthcare_cost || 0) + 
                   (city.internet_cost || 0);
  
  const headlines = {
    costOfLiving: `Cost of Living in ${city.name} (${currentYear}) – Complete Breakdown`,
    salaryNeeded: `Salary Needed to Live in ${city.name} (${currentYear})`,
    livingGuide: `Living in ${city.name} (${currentYear}) – Complete Expat Guide`
  };
  
  const descriptions = {
    costOfLiving: `Detailed breakdown of living costs in ${city.name}, ${city.country_name || ''}. Total estimated: $${totalCost}/month.`,
    salaryNeeded: `Calculate the minimum salary needed to live comfortably in ${city.name}.`,
    livingGuide: `Everything you need to know about living in ${city.name}. Pros, cons, tips, and more.`
  };
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headlines[pageType] || `Guide to ${city.name}`,
    "description": descriptions[pageType] || city.description,
    "author": {
      "@type": "Organization",
      "name": "MoveAbroadIQ",
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "MoveAbroadIQ",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo.png`
      }
    },
    "datePublished": today,
    "dateModified": today,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}${url}`
    },
    "about": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "Country",
        "name": city.country_name || ''
      }
    },
    "image": city.image_url
  };
};

// Generate Schema.org WebApplication for Calculator
export const generateCalculatorSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "MoveAbroadIQ Relocation Cost Calculator",
    "description": "Calculate your monthly living costs for any city based on family size and lifestyle preferences.",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Cost estimation by city",
      "Family size adjustment",
      "Lifestyle level selection",
      "Detailed cost breakdown"
    ],
    "author": {
      "@type": "Organization",
      "name": "MoveAbroadIQ"
    }
  };
};

// Generate Schema.org for Compare Tool
export const generateCompareToolSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "MoveAbroadIQ Country Comparison Tool",
    "description": "Compare up to 3 countries side by side across cost of living, taxes, visa requirements, safety, and more.",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "MoveAbroadIQ"
    }
  };
};

export const generateCityMeta = (city, pageType) => {
  const currentYear = new Date().getFullYear();
  const cityName = city?.name || 'City';
  const countryName = city?.country_name || '';
  const totalCost = (city?.rent_1br || 0) + (city?.groceries_cost || 0) + 
                   (city?.transport_cost || 0) + (city?.healthcare_cost || 0) + 
                   (city?.internet_cost || 0);
  
  const meta = {
    overview: {
      title: `${cityName}, ${countryName} - Cost of Living, Safety & NomadIQ Score (${currentYear})`,
      description: `Complete guide to living in ${cityName}. Real costs for rent ($${city?.rent_1br}/mo), groceries, transport, healthcare. Safety score: ${city?.safety_score}/10. NomadIQ Score: ${city?.nomad_score?.toFixed(1)}/10.`,
      canonicalPath: `/cities/${city?.slug}`
    },
    costOfLiving: {
      title: `Cost of Living in ${cityName} (${currentYear}) – Rent, Food, Transport`,
      description: `Discover the real cost of living in ${cityName}, ${countryName}. Average rent: $${city?.rent_1br}/mo. Monthly groceries: $${city?.groceries_cost}. Transport: $${city?.transport_cost}. Total estimated: $${totalCost}/month.`,
      canonicalPath: `/cost-of-living/${city?.slug}`
    },
    salaryNeeded: {
      title: `Salary Needed to Live in ${cityName} (${currentYear}) – Income Calculator`,
      description: `Calculate the minimum salary needed to live in ${cityName}. Basic lifestyle: $${Math.round(totalCost * 0.7 * 1.3)}/mo. Comfortable: $${Math.round(totalCost * 1.3)}/mo. Premium: $${Math.round(totalCost * 1.5 * 1.3)}/mo.`,
      canonicalPath: `/salary-needed/${city?.slug}`
    },
    livingGuide: {
      title: `Living in ${cityName} (${currentYear}) – Complete Expat Guide`,
      description: `Everything you need to know about living in ${cityName}, ${countryName}. Pros, cons, best neighborhoods, cost overview, safety (${city?.safety_score}/10), internet speed (${city?.internet_speed} Mbps), and relocation tips.`,
      canonicalPath: `/living-in/${city?.slug}`
    }
  };
  
  return meta[pageType] || meta.overview;
};
